import {
  Button,
  Center,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Spinner,
  Text,
  Stack,
  Textarea,
  useDisclosure,
  Icon,
  Box,
  AlertDialog,
  Divider,
  AlertDialogBody,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogFooter,
  useToast,
} from "@chakra-ui/react";

import PageTitle from "generics/PageTitle";
import ToolbarPortal from "generics/ToolbarPortal";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { useAddService, useEditService, useService } from "api/services";
import { useLabTests } from "api/tests";
import { FaChevronDown } from "react-icons/fa";

const defaultService = {
  fee: 100,
  name: "",
  description: "",
};

export default function AddService({ edit }: { edit?: boolean | undefined }) {
  const { handleSubmit, register } = useForm();

  const [addService, addServiceInfo] = useAddService();
  const [editService, editServiceInfo] = useEditService();
  const labTestsQuery = useLabTests();
  const [selectedService, setSelectedService] = useState<any>(defaultService);
  const { serviceId } = useParams();
  const serviceInfo = useService(serviceId);
  let [availableTests, setAvailableTests] = useState<any>([]);
  const [labTestsMap, setLabTestsMap] = useState<any>({});

  const navigate = useNavigate();
  const { onOpen, isOpen, onClose } = useDisclosure();

  const toast = useToast();

  useEffect(() => {
    if (serviceInfo.data) setSelectedService(serviceInfo.data);
  }, [serviceInfo.data]);

  useEffect(() => {
    if (labTestsQuery.data) {
      labTestsQuery?.data?.forEach((test) => {
        // if (labTestsMap[test?.id]) {
        labTestsMap[test?.id] = test;
        // }
      });
      setLabTestsMap(labTestsMap);
    }
    // eslint-disable-next-line
  }, [labTestsQuery.data]);

  useEffect(() => {
    if (selectedService.tests) {
      availableTests = [];
      labTestsQuery?.data?.forEach((test) => {
        if (!selectedService.tests?.includes(test.id)) {
          availableTests.push(test);
        }
      });
      setAvailableTests(availableTests);
    } else {
      setAvailableTests(labTestsQuery?.data);
    }
  }, [selectedService, labTestsQuery.data]);

  function selectTest(selectedTest: any) {
    if (!selectedService.tests) {
      selectedService.tests = [];
    }
    if (selectedService.tests?.includes(selectedTest.id)) {
      selectedService.tests = selectedService.tests.filter((testId: any) => {
        return testId !== selectedTest.id;
      });
    } else {
      selectedService.tests.push(selectedTest.id);
    }
    setSelectedService({ ...selectedService });
  }

  function removeTest(testId: string) {
    if (selectedService.tests?.includes(testId)) {
      selectedService.tests = selectedService.tests.filter((id: any) => {
        return id !== testId;
      });
      setSelectedService({ ...selectedService });
    }
  }

  async function onSubmit(values: any) {
    const { name, description } = values;
    const tests = selectedService.tests ?? [];

    try {
      if (edit) {
        await editService({
          ...selectedService,
          name,
          description,
          tests,
        });
      } else {
        await addService({
          ...selectedService,
          name,
          description,
          tests,
        });
      }
      navigate("/services");
    } catch (e) {
      toast({
        status: "error",
        title: "Error !!",
        description: e.message,
        duration: 3000,
        isClosable: true,
      });
      console.error(e);
    }
  }

  function getSelectedTestsChips() {
    return (
      <Flex wrap="wrap">
        {selectedService.tests.map((testId: string) => {
          return (
            <Box
              cursor="pointer"
              onClick={(e) => {
                e.stopPropagation();
                removeTest(testId);
              }}
              key={testId}
              borderRadius="24px"
              bg="gray.200"
              color="highlight.500"
              px="4"
              m="1"
            >
              {labTestsMap[testId]?.name || "Deleted Test"}
            </Box>
          );
        })}
      </Flex>
    );
  }

  if (serviceInfo.isLoading) {
    return <Spinner />;
  }

  if (serviceId && serviceInfo.status === "success" && !serviceInfo.data) {
    navigate("/services");
    return <Flex></Flex>;
  }

  return (
    <Flex direction="column" padding="8">
      <ToolbarPortal>
        <Flex justifyContent="space-between" alignItems="center">
          <Flex justify="space-between" flexGrow={1} align="center">
            <PageTitle flexGrow={1}>
              {edit ? `Update ${selectedService?.name}` : "Add New Service"}
            </PageTitle>
          </Flex>
        </Flex>
      </ToolbarPortal>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex direction="column">
          {labTestsQuery.isLoading ? (
            <Center w="100%" h="100%">
              <Spinner size="lg" />
            </Center>
          ) : (
            <Stack>
              <FormControl as="fieldset">
                <FormLabel as="legend">Name</FormLabel>
                <Input
                  ref={register}
                  defaultValue={selectedService?.name}
                  name="name"
                  placeholder="Name of Service"
                  required
                />
              </FormControl>
              <FormControl as="fieldset">
                <FormLabel as="legend">Description</FormLabel>
                <Textarea
                  ref={register}
                  defaultValue={selectedService?.description}
                  name="description"
                  placeholder="Description"
                />
              </FormControl>
              <FormControl as="fieldset">
                <FormLabel as="legend">Select Tests</FormLabel>
                <Flex
                  cursor="pointer"
                  align="center"
                  justify="space-between"
                  width="100%"
                  mb="2"
                  py="2"
                  px="4"
                  borderRadius="3px"
                  border="1px solid"
                  borderColor="border.500"
                  onClick={onOpen}
                >
                  {(!selectedService.tests ||
                    selectedService.tests.length === 0) && (
                    <Text color="gray.400">Tap to select</Text>
                  )}
                  {selectedService.tests &&
                    selectedService.tests.length !== 0 &&
                    getSelectedTestsChips()}
                  <Icon
                    as={FaChevronDown}
                    color="highlight.500"
                    onClick={onOpen}
                  ></Icon>
                </Flex>
              </FormControl>
            </Stack>
          )}
        </Flex>

        <Flex my="3">
          <Button
            mr={3}
            type="submit"
            isLoading={addServiceInfo.isLoading || editServiceInfo.isLoading}
          >
            {edit ? "Update Service" : "Add Service"}
          </Button>
        </Flex>
      </form>
      <SelectorDialog
        isOpen={isOpen}
        onClose={onClose}
        availableTests={availableTests}
        selectTest={selectTest}
      ></SelectorDialog>
    </Flex>
  );
}

function SelectorDialog({
  isOpen,
  onClose,
  availableTests = [],
  selectTest,
}: {
  isOpen: boolean;
  onClose: any;
  availableTests: any;
  selectTest: Function;
}) {
  const cancelRef = React.useRef<any>();
  const [selectedTest, setSelectedTest] = useState<any>();

  useEffect(() => {
    if (availableTests && availableTests.length !== 0) {
      setSelectedTest(availableTests[0]);
    }
    if (!availableTests || availableTests.length === 0) {
      setSelectedTest(undefined);
    }

    // eslint-disable-next-line
  }, [availableTests]);
  return (
    <AlertDialog
      motionPreset="slideInBottom"
      leastDestructiveRef={cancelRef}
      onClose={onClose}
      isOpen={isOpen}
      isCentered
    >
      <AlertDialogOverlay></AlertDialogOverlay>
      <AlertDialogContent maxW="80vw">
        {!availableTests && availableTests.length === 0 && (
          <Center>
            <Text
              p="4"
              fontSize="16px"
              color="settingText.500"
              fontWeight="400"
            >
              No Tests Found, Goto to tests tab to add new tests
            </Text>
          </Center>
        )}
        <AlertDialogBody padding="0" maxH="70vh" display="flex">
          <Flex>
            <Flex flex={1} direction="column" overflowY="scroll">
              {availableTests
                .sort((a: any, b: any) =>
                  a.name < b.name ? -1 : a.name > b.name ? 1 : 0
                )
                .map((test: any) => {
                  return (
                    <Box
                      key={test.id}
                      margin="0"
                      padding="2"
                      backgroundColor={
                        selectedTest?.id === test.id
                          ? "containerBg.500"
                          : "white"
                      }
                      _hover={{ backgroundColor: "containerBg.500" }}
                      cursor="pointer"
                      onClick={() => {
                        setSelectedTest(test);
                      }}
                    >
                      <Text size="sm" color="highlight.500" fontWeight="400">
                        {test.name ?? "No name"}
                      </Text>
                    </Box>
                  );
                })}
            </Flex>
            <Flex
              flex={2}
              fontSize="16px"
              color="settingText.500"
              fontWeight="400"
              p="8"
            >
              {selectedTest
                ? selectedTest.description || "No description found."
                : "No tests found"}
            </Flex>
          </Flex>
        </AlertDialogBody>
        <Divider></Divider>
        <AlertDialogFooter>
          <Button
            variant="outline"
            border="2px"
            borderColor="border.500"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            ml={3}
            bg="primary.500"
            onClick={async () => {
              if (selectedTest) {
                selectTest(selectedTest);
              }
              onClose?.();
            }}
          >
            Confirm
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
